'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useBookings } from '@/hooks/useBookings';
import { Button } from '../ui/Button';
import {
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  ChevronLeft,
  ChevronRight,
  User,
  Users,
  CalendarCheck,
  Loader2,
  Home,
  Video,
} from 'lucide-react';
import type { Booking } from '@/types/customer';
import { cn } from '@/lib/utils';

export const Bookings: React.FC = () => {
  const { bookings, isLoading, cancelBooking, rescheduleBooking } = useBookings();
  const pathname = usePathname();
  const localeMatch = pathname.match(/^\/([a-z]{2})(\/|$)/);
  const locale = localeMatch ? localeMatch[1] : 'en';
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past' | 'cancelled'>('upcoming');
  const [currentDate, setCurrentDate] = useState(new Date());

  // Filter logic
  const filteredBookings = bookings.filter((b) => {
    if (activeTab === 'upcoming') return b.status === 'CONFIRMED' || b.status === 'PENDING';
    if (activeTab === 'past') return b.status === 'COMPLETED';
    if (activeTab === 'cancelled') return b.status === 'CANCELLED';
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="animate-[fadeIn_0.3s_ease-out] pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-semibold text-stone-900 tracking-tight">Bookings</h2>
          <p className="text-stone-500 mt-2 text-lg font-light">
            Manage your viewings and appointments.
          </p>
        </div>
        <Link href={`/${locale}/properties`}>
          <Button className="bg-stone-900 text-white shadow-xl shadow-stone-200">
            <CalendarCheck size={18} className="mr-2" /> Book New Viewing
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        {/* LEFT COL: Booking List */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="flex p-1 bg-stone-100 rounded-xl w-fit">
            {(['upcoming', 'past', 'cancelled'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-lg capitalize transition-all duration-200',
                  activeTab === tab
                    ? 'bg-white text-stone-900 shadow-sm'
                    : 'text-stone-500 hover:text-stone-900'
                )}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="space-y-4">
            {filteredBookings.length > 0 ? (
              filteredBookings.map((booking) => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  onCancel={cancelBooking}
                  onReschedule={rescheduleBooking}
                />
              ))
            ) : (
              <EmptyState tab={activeTab} />
            )}
          </div>
        </div>

        {/* RIGHT COL: Calendar Widget & Stats */}
        <div className="space-y-6">
          {/* Mini Calendar */}
          <div className="bg-white rounded-3xl border border-stone-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-stone-900">
                {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </h3>
              <div className="flex gap-1">
                <button
                  onClick={() =>
                    setCurrentDate(
                      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
                    )
                  }
                  className="p-1 hover:bg-stone-100 rounded-md text-stone-500"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() =>
                    setCurrentDate(
                      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
                    )
                  }
                  className="p-1 hover:bg-stone-100 rounded-md text-stone-500"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
            <MiniCalendar currentMonth={currentDate} bookings={bookings} />

            <div className="mt-6 pt-6 border-t border-stone-100">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-stone-500">Upcoming viewings</span>
                <span className="font-semibold text-stone-900">
                  {bookings.filter((b) => b.status === 'CONFIRMED' || b.status === 'PENDING').length}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Completed</span>
                <span className="font-semibold text-stone-900">
                  {bookings.filter((b) => b.status === 'COMPLETED').length}
                </span>
              </div>
            </div>
          </div>

          {/* Help Card */}
          <div className="bg-stone-900 rounded-3xl p-6 text-white relative overflow-hidden">
            <div className="relative z-10">
              <h4 className="font-semibold text-lg mb-2">Need to reschedule?</h4>
              <p className="text-stone-400 text-sm mb-4">
                Contact your dedicated agent directly if you need to make last minute changes.
              </p>
              <Link href={`/${locale}/account/enquiries`}>
                <Button size="sm" className="bg-white text-stone-900 hover:bg-stone-100 border-none">
                  Contact Agent
                </Button>
              </Link>
            </div>
            {/* Decorative circle */}
            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-stone-800 rounded-full blur-2xl" />
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Helpers ---

const BOOKING_TYPE_LABELS: Record<string, string> = {
  VIEWING: 'Property Viewing',
  VALUATION: 'Valuation',
  CONSULTATION: 'Consultation',
  MEETING: 'Meeting',
};

const BOOKING_TYPE_ICONS: Record<string, React.ReactNode> = {
  VIEWING: <Home size={14} />,
  MEETING: <Video size={14} />,
  CONSULTATION: <Users size={14} />,
  VALUATION: <MapPin size={14} />,
};

const HOST_ROLES = ['BOOKING_HOST', 'EVENT_HOST', 'LISTING_AGENT', 'AGENT'];

function getAgentFromStakeholders(booking: Booking): { name: string; initials: string } | null {
  const host = booking.stakeholders?.find((s) => HOST_ROLES.includes(s.role) && !s.isCurrentUser);
  if (!host) return null;
  const parts = host.name.trim().split(/\s+/);
  const initials = parts.length >= 2
    ? `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase()
    : (parts[0]?.[0] ?? '').toUpperCase();
  return { name: host.name, initials };
}

// --- SUB-COMPONENT: Booking Card ---
const BookingCard: React.FC<{
  booking: Booking;
  onCancel: (bookingId: string, reason?: string) => Promise<Booking | null>;
  onReschedule: (bookingId: string, newScheduledAt: string) => Promise<Booking | null>;
}> = ({ booking, onCancel, onReschedule }) => {
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [showRescheduleInput, setShowRescheduleInput] = useState(false);
  const [newDateTime, setNewDateTime] = useState('');
  const dateObj = new Date(booking.scheduledAt);
  const isPast = booking.status === 'COMPLETED';
  const isCancelled = booking.status === 'CANCELLED';

  const agent = useMemo(() => getAgentFromStakeholders(booking), [booking]);
  const typeLabel = booking.subject || BOOKING_TYPE_LABELS[booking.bookingType] || booking.bookingType;
  const typeIcon = BOOKING_TYPE_ICONS[booking.bookingType] ?? <MapPin size={14} />;
  const subtypeLabel = BOOKING_TYPE_LABELS[booking.bookingType] || 'Appointment';

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel this booking?')) return;
    setIsCancelling(true);
    try {
      await onCancel(booking.id, 'Cancelled by customer');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReschedule = async () => {
    if (!newDateTime) return;
    setIsRescheduling(true);
    try {
      const result = await onReschedule(booking.id, new Date(newDateTime).toISOString());
      if (result) {
        setShowRescheduleInput(false);
        setNewDateTime('');
      }
    } finally {
      setIsRescheduling(false);
    }
  };

  return (
    <div
      className={cn(
        'bg-white rounded-2xl border transition-all duration-300 group overflow-hidden flex flex-col md:flex-row',
        isCancelled
          ? 'opacity-70 border-stone-100'
          : 'border-stone-200 hover:border-stone-300 hover:shadow-lg'
      )}
    >
      {/* Left Status Strip */}
      <div
        className={cn(
          'w-full md:w-1.5 h-1.5 md:h-auto',
          isCancelled ? 'bg-red-400' : isPast ? 'bg-stone-300' : 'bg-stone-900'
        )}
      />

      <div className="p-6 flex-1 flex flex-col md:flex-row gap-6">
        {/* Date Block */}
        <div className="flex md:flex-col items-center justify-center min-w-[4rem] gap-2 md:gap-0 border-b md:border-b-0 md:border-r border-stone-100 pb-4 md:pb-0 md:pr-6">
          <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">
            {dateObj.toLocaleString('default', { month: 'short' })}
          </span>
          <span className="text-3xl font-light text-stone-900">{dateObj.getDate()}</span>
          <span className="text-xs text-stone-400 font-medium">
            {dateObj.toLocaleString('default', { weekday: 'short' })}
          </span>
        </div>

        {/* Info Block */}
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h3
                className={cn(
                  'font-semibold text-lg',
                  isCancelled ? 'text-stone-500 line-through' : 'text-stone-900'
                )}
              >
                {typeLabel}
              </h3>
              <p className="text-sm text-stone-500 flex items-center gap-1 mt-1">
                {typeIcon} {subtypeLabel}
              </p>
            </div>
            {/* Agent info from stakeholders */}
            <div className="hidden sm:block text-right">
              <div className="flex items-center gap-2 justify-end">
                {agent ? (
                  <>
                    <div className="text-right">
                      <p className="text-xs font-bold text-stone-900">{agent.name}</p>
                      <p className="text-[10px] text-stone-500 uppercase">Agent</p>
                    </div>
                    <div className="w-9 h-9 rounded-full border border-stone-200 bg-stone-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-stone-600">{agent.initials}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-right">
                      <p className="text-xs font-bold text-stone-900">Agent</p>
                      <p className="text-[10px] text-stone-500 uppercase">Pending</p>
                    </div>
                    <div className="w-9 h-9 rounded-full border border-stone-200 bg-stone-50 flex items-center justify-center">
                      <User size={16} className="text-stone-400" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-4">
            <div className="bg-stone-50 px-3 py-1.5 rounded-lg border border-stone-100 flex items-center gap-2 text-sm text-stone-700">
              <Clock size={14} className="text-stone-400" />
              <span className="font-medium">
                {dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="text-stone-300">|</span>
              <span className="text-stone-500">{booking.duration} mins</span>
            </div>
            <div className="bg-stone-50 px-3 py-1.5 rounded-lg border border-stone-100 flex items-center gap-2 text-sm text-stone-700">
              <User size={14} />
              <span className="capitalize">
                {booking.bookingType === 'MEETING' ? 'Meeting' : 'In-Person'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions - Only for Active */}
      {!isPast && !isCancelled && (
        <div className="p-4 md:p-6 bg-stone-50 border-t md:border-t-0 md:border-l border-stone-100 flex flex-col justify-center gap-2">
          {showRescheduleInput ? (
            <div className="flex flex-col gap-2">
              <input
                type="datetime-local"
                value={newDateTime}
                onChange={(e) => setNewDateTime(e.target.value)}
                className="text-sm border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-stone-900/10"
                min={new Date().toISOString().slice(0, 16)}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex-1 bg-stone-900 text-white"
                  onClick={handleReschedule}
                  disabled={!newDateTime || isRescheduling}
                >
                  {isRescheduling ? <Loader2 size={14} className="animate-spin" /> : 'Confirm'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-white border-stone-200"
                  onClick={() => { setShowRescheduleInput(false); setNewDateTime(''); }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                className="md:w-32 bg-white border-stone-200 text-stone-600"
                onClick={() => setShowRescheduleInput(true)}
              >
                Reschedule
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="md:w-32 bg-white border-stone-200 text-stone-600 hover:text-red-600 hover:border-red-200"
                onClick={handleCancel}
                disabled={isCancelling}
              >
                {isCancelling ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Cancel
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// --- SUB-COMPONENT: Mini Calendar ---
const MiniCalendar: React.FC<{ currentMonth: Date; bookings: Booking[] }> = ({
  currentMonth,
  bookings,
}) => {
  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();
  const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();

  // Adjust for Monday start (standard UK/EU)
  const startOffset = firstDay === 0 ? 6 : firstDay - 1;

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const blanks = Array.from({ length: startOffset }, (_, i) => i);

  const hasBooking = (day: number) => {
    return bookings.some((b) => {
      const bookingDate = new Date(b.scheduledAt);
      return (
        bookingDate.getDate() === day &&
        bookingDate.getMonth() === currentMonth.getMonth() &&
        bookingDate.getFullYear() === currentMonth.getFullYear()
      );
    });
  };

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 text-center mb-2">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <span key={i} className="text-xs font-bold text-stone-300">
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {blanks.map((b) => (
          <div key={`b-${b}`} className="h-9 w-9" />
        ))}
        {days.map((d) => {
          const active = hasBooking(d);
          const isToday =
            d === new Date().getDate() &&
            currentMonth.getMonth() === new Date().getMonth() &&
            currentMonth.getFullYear() === new Date().getFullYear();
          return (
            <div
              key={d}
              className={cn(
                'h-9 w-9 rounded-full flex items-center justify-center text-sm cursor-pointer transition-colors relative',
                isToday && 'bg-stone-900 text-white font-bold',
                !isToday && active && 'bg-stone-100 text-stone-900 font-bold border border-stone-200',
                !isToday && !active && 'text-stone-500 hover:bg-stone-50'
              )}
            >
              {d}
              {active && !isToday && (
                <div className="absolute w-1 h-1 bg-green-500 rounded-full mt-5" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EmptyState: React.FC<{ tab: string }> = ({ tab }) => (
  <div className="bg-stone-50 rounded-2xl border border-stone-200 border-dashed p-12 text-center">
    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 border border-stone-100 shadow-sm">
      <CalendarIcon size={24} className="text-stone-300" />
    </div>
    <h3 className="text-stone-900 font-medium">No {tab} bookings</h3>
    <p className="text-stone-500 text-sm mt-1">
      You don&apos;t have any appointments in this category.
    </p>
  </div>
);
